import React, { useEffect, useRef, useState } from "react";
import { MapPin, Building2 } from "lucide-react";

const BusinessLocationForm = ({ form, setForm }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef(null);
    const skipNextSearch = useRef(false);
    const GOOGLE_API_KEY = "AIzaSyDuC2ckkWZrt8NhYQwg6LCOuF7Usho4aU8";
    const [isFocused, setIsFocused] = useState(false);


    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const searchAddress = async (query) => {
        if (!query || query.length < 3) {
            setSuggestions([]);
            return;
        }

        try {
            setLoading(true);

            const response = await fetch(
                "https://places.googleapis.com/v1/places:autocomplete",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Goog-Api-Key": GOOGLE_API_KEY,
                    },
                    body: JSON.stringify({
                        input: query,
                        regionCode: "ZA"
                    }),
                }
            );

            const data = await response.json();

            console.log(data);

            setSuggestions(data.suggestions || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isFocused) return;

        if (skipNextSearch.current) {
            skipNextSearch.current = false;
            return;
        }

        clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
            searchAddress(form.streetAddress);
        }, 400);

        return () => clearTimeout(debounceRef.current);
    }, [form.streetAddress, isFocused]);

    const selectSuggestion = async (prediction) => {
        skipNextSearch.current = true;

        setSuggestions([]);
        setIsFocused(false);

        setForm(prev => ({
            ...prev,
            placeId: prediction.placeId,
        }));

        await getPlaceDetails(prediction.placeId);
    };


    const getPlaceDetails = async (placeId) => {
        try {
            const response = await fetch(
                `https://places.googleapis.com/v1/places/${placeId}`,
                {
                    headers: {
                        "X-Goog-Api-Key": GOOGLE_API_KEY,
                        "X-Goog-FieldMask":
                            "formattedAddress,addressComponents,rating,userRatingCount,reviews",
                    },
                }
            );

            const place = await response.json();

            const components = place.addressComponents || [];

            const getComponent = (type) =>
                components.find((c) => Array.isArray(c.types) && c.types.includes(type))
                    ?.longText || "";

            if (!place.formattedAddress) {
                console.error("No formatted address found for the selected place.");
                return;
            }

            if (!place.rating && !place.userRatingCount) {
                console.warn("No rating or user rating count found for the selected place.");
                setForm((prev) => ({
                    ...prev,
                    streetAddress: place.formattedAddress || prev.streetAddress,
                    city:
                        getComponent("locality") ||
                        getComponent("postal_town"),
                    province: getComponent("administrative_area_level_1"),
                    postalCode: getComponent("postal_code"),
                    reviews: 0,
                    userRatingCount: 0,
                }));
                return;
            }

            setForm((prev) => ({
                ...prev,
                streetAddress: place.formattedAddress || prev.streetAddress,
                city:
                    getComponent("locality") ||
                    getComponent("postal_town"),
                province: getComponent("administrative_area_level_1"),
                postalCode: getComponent("postal_code"),
                reviews: place?.rating || 0,
                userRatingCount: place.userRatingCount || 0,
            }));
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div className="bg-white rounded-[16px] border border-[#e4e5e7] p-[24px] md:p-[32px] flex flex-col gap-[20px]">

            {/* Street Address */}
            <div className="flex flex-col gap-[6px]">
                <label className="font-semibold text-[14px] text-[#212326]">
                    Street Address *
                </label>

                <div className="relative">
                    <MapPin className="absolute left-[14px] top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#9298a0]" />

                    <input
                        type="text"
                        name="streetAddress"
                        placeholder="44 Bird Street"
                        value={form.streetAddress || ""}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => {
                            setTimeout(() => {
                                setIsFocused(false);
                                setSuggestions([]);
                            }, 150); // allows click on suggestion
                        }}
                        onChange={handleChange}
                        className="w-full h-[48px] rounded-[12px] border border-[#e4e5e7] pl-[40px] pr-[16px] font-semibold text-[14px] text-[#212326] placeholder:text-[#9298a0] focus:outline-none focus:border-[#ff5323] transition-colors"
                    />
                    {loading && (
                        <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg p-3 shadow w-full z-50">
                            Searching...
                        </div>
                    )}

                    {suggestions.length > 0 && (
                        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-[#e4e5e7] rounded-xl shadow-lg z-50 overflow-hidden">
                            {suggestions.map((item, index) => {
                                const prediction = item.placePrediction;

                                return (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => selectSuggestion(prediction)}
                                        className="w-full text-left px-4 py-3 hover:bg-gray-100 border-b last:border-b-0"
                                    >
                                        <div className="font-semibold text-sm">
                                            {prediction.structuredFormat.mainText.text}
                                        </div>

                                        <div className="text-xs text-gray-500">
                                            {prediction.structuredFormat.secondaryText?.text}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* City + Province */}
            <div className="flex flex-col md:flex-row gap-[16px]">

                {/* City */}
                <div className="flex-1 flex flex-col gap-[6px]">
                    <label className="font-semibold text-[14px] text-[#212326]">
                        City / Town *
                    </label>

                    <div className="relative">
                        <Building2 className="absolute left-[14px] top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#9298a0]" />

                        <input
                            type="text"
                            name="city"
                            placeholder="Stellenbosch"
                            value={form.city || ""}
                            onChange={handleChange}
                            className="w-full h-[48px] rounded-[12px] border border-[#e4e5e7] pl-[40px] pr-[16px] font-semibold text-[14px] text-[#212326] placeholder:text-[#9298a0] focus:outline-none focus:border-[#ff5323] transition-colors"
                        />
                    </div>
                </div>

                {/* Province */}
                <div className="flex-1 flex flex-col gap-[6px]">
                    <label className="font-semibold text-[14px] text-[#212326]">
                        Province
                    </label>

                    <select
                        name="province"
                        value={form.province || ""}
                        onChange={handleChange}
                        className="w-full h-[48px] rounded-[12px] border border-[#e4e5e7] px-[16px] font-semibold text-[14px] text-[#212326] focus:outline-none focus:border-[#ff5323] transition-colors bg-white appearance-none cursor-pointer"
                    >
                        <option value="">Select province</option>
                        <option value="Eastern Cape">Eastern Cape</option>
                        <option value="Free State">Free State</option>
                        <option value="Gauteng">Gauteng</option>
                        <option value="KwaZulu-Natal">KwaZulu-Natal</option>
                        <option value="Limpopo">Limpopo</option>
                        <option value="Mpumalanga">Mpumalanga</option>
                        <option value="North West">North West</option>
                        <option value="Northern Cape">Northern Cape</option>
                        <option value="Western Cape">Western Cape</option>
                    </select>
                </div>
            </div>

            {/* Postal Code */}
            <div className="flex flex-col gap-[6px] max-w-[200px]">
                <label className="font-semibold text-[14px] text-[#212326]">
                    Postal Code
                </label>

                <input
                    type="text"
                    name="postalCode"
                    placeholder="7600"
                    maxLength={5}
                    value={form.postalCode || ""}
                    onChange={handleChange}
                    className="w-full h-[48px] rounded-[12px] border border-[#e4e5e7] px-[16px] font-semibold text-[14px] text-[#212326] placeholder:text-[#9298a0] focus:outline-none focus:border-[#ff5323] transition-colors"
                />
            </div>

            {/* {form.reviews > 0 && (
                <div className="mt-3 rounded-xl border border-[#e4e5e7] bg-white p-4">
                    <h3 className="font-semibold text-[#212326]">
                        Google Rating
                    </h3>

                    <div className="mt-2 flex items-center gap-2">
                        <span className="text-2xl">⭐</span>

                        <span className="text-xl font-bold">
                            {form.reviews}
                        </span>

                        <span className="text-sm text-gray-500">
                            ({form.userRatingCount} reviews)
                        </span>
                    </div>
                </div>
            )} */}
        </div>
    );
};

export default BusinessLocationForm;